import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SEO from "../components/seo/SEO";
import { Section, Fade, Heading, Label, Text, Button, C } from "../components/ui";
import { SOCIAL, AUTO, WEB } from "../data";

export default function Pricing() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"social" | "automation" | "web">("social");

  const goToContact = (plan?: string) => {
    if (plan) {
      navigate("/contact?plan=" + plan);
    } else {
      navigate("/contact");
    }
  };

  const tabs = [
    { id: "social", label: "Social Media" },
    { id: "automation", label: "Automation" },
    { id: "web", label: "Web" },
  ];

  const getPackages = () => {
    switch (activeTab) {
      case "social":
        return SOCIAL;
      case "automation":
        return AUTO;
      case "web":
        return WEB;
      default:
        return SOCIAL;
    }
  };

  return (
    <>
      <SEO
        title="Pricing"
        description="Transparent pricing for done-for-you marketing services. Social media, automation, and web development packages for local businesses."
      />

      {/* Hero Section */}
      <Section background={C.navy}>
        <Fade>
          <div style={{ textAlign: "center", maxWidth: "800px", margin: "0 auto" }}>
            <Label light>Pricing</Label>
            <Heading light style={{ marginBottom: "24px" }}>
              Simple, Transparent Pricing
            </Heading>
            <Text light size="lg" style={{ marginBottom: "40px" }}>
              Choose the package that fits your needs. All packages include dedicated support
              and monthly reporting. No hidden fees, no long-term contracts.
            </Text>
            <Button variant="coral" onClick={() => goToContact()}>
              Not Sure? Get a Free Consultation
            </Button>
          </div>
        </Fade>
      </Section>

      {/* Pricing Tabs */}
      <Section background={C.offWhite} padding="40px 24px">
        <Fade>
          <div style={{ display: "flex", justifyContent: "center", gap: "16px", flexWrap: "wrap" }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                style={{
                  padding: "12px 24px",
                  background: activeTab === tab.id ? C.navy : C.white,
                  color: activeTab === tab.id ? C.white : C.gray600,
                  border: `1px solid ${activeTab === tab.id ? C.navy : C.gray200}`,
                  borderRadius: "8px",
                  fontFamily: C.font,
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </Fade>
      </Section>

      {/* Pricing Cards */}
      <Section background={C.offWhite}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "32px",
            marginTop: "32px",
          }}
        >
          {getPackages().map((pkg, index) => (
            <Fade key={pkg.id} delay={index * 0.1}>
              <div
                style={{
                  position: "relative",
                  background: pkg.hot ? C.navy : C.white,
                  border: `2px solid ${pkg.hot ? C.navy : C.gray100}`,
                  borderRadius: "16px",
                  padding: "32px 28px",
                  transition: "all 0.3s ease",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                {pkg.hot && (
                  <div
                    style={{
                      position: "absolute",
                      top: "-12px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      background: C.coral,
                      color: C.white,
                      padding: "6px 16px",
                      borderRadius: "20px",
                      fontFamily: C.mono,
                      fontSize: "11px",
                      fontWeight: 600,
                      letterSpacing: "0.05em",
                    }}
                  >
                    POPULAR
                  </div>
                )}

                <div style={{ marginBottom: "24px" }}>
                  <Heading
                    size={3}
                    style={{
                      marginBottom: "8px",
                      color: pkg.hot ? C.white : C.navy,
                    }}
                  >
                    {pkg.name}
                  </Heading>
                  <Text
                    size="sm"
                    style={{
                      color: pkg.hot ? C.gray300 : C.gray400,
                      marginBottom: "16px",
                    }}
                  >
                    {pkg.sub}
                  </Text>
                  <div
                    style={{
                      fontSize: "2rem",
                      fontWeight: 600,
                      color: pkg.hot ? C.white : C.navy,
                      marginBottom: "4px",
                    }}
                  >
                    {pkg.price}
                  </div>
                  <Text
                    size="sm"
                    style={{
                      color: pkg.hot ? C.gray400 : C.gray500,
                    }}
                  >
                    {pkg.per}
                  </Text>
                </div>

                <div style={{ marginBottom: "24px", flex: 1 }}>
                  <Text
                    size="sm"
                    style={{
                      color: pkg.hot ? C.gray300 : C.gray500,
                      marginBottom: "12px",
                      fontWeight: 600,
                    }}
                  >
                    BEST FOR:
                  </Text>
                  <Text
                    size="sm"
                    style={{
                      color: pkg.hot ? C.gray200 : C.gray600,
                      marginBottom: "16px",
                    }}
                  >
                    {pkg.best}
                  </Text>

                  <Text
                    size="sm"
                    style={{
                      color: pkg.hot ? C.gray300 : C.gray500,
                      marginBottom: "12px",
                      fontWeight: 600,
                    }}
                  >
                    INCLUDES:
                  </Text>
                  <ul
                    style={{
                      margin: 0,
                      paddingLeft: "20px",
                      listStyle: "none",
                    }}
                  >
                    {pkg.inc.map((item, idx) => (
                      <li
                        key={idx}
                        style={{
                          position: "relative",
                          marginBottom: "8px",
                          fontSize: "14px",
                          color: pkg.hot ? C.gray200 : C.gray600,
                          lineHeight: 1.5,
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            left: "-20px",
                            color: pkg.hot ? C.coral : C.coral,
                            fontWeight: "bold",
                          }}
                        >
                          ✓
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {pkg.out && (
                  <div
                    style={{
                      padding: "12px",
                      background: pkg.hot ? "rgba(255, 255, 255, 0.1)" : C.gray50,
                      borderRadius: "8px",
                      marginBottom: "20px",
                    }}
                  >
                    <Text
                      size="sm"
                      style={{
                        color: pkg.hot ? C.gray200 : C.gray600,
                        margin: 0,
                        fontStyle: "italic",
                      }}
                    >
                      "{pkg.out}"
                    </Text>
                  </div>
                )}

                <Button
                  variant={pkg.hot ? "coral" : "primary"}
                  size="default"
                  onClick={() => goToContact(pkg.id)}
                  style={{ width: "100%" }}
                >
                  Get Started
                </Button>
              </div>
            </Fade>
          ))}
        </div>
      </Section>

      {/* FAQ Section */}
      <Section background={C.white}>
        <Fade>
          <div style={{ textAlign: "center", marginBottom: "60px" }}>
            <Label>Frequently Asked Questions</Label>
            <Heading size={2}>Common Questions</Heading>
          </div>
        </Fade>

        <div
          style={{
            maxWidth: "700px",
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          {[
            {
              q: "Do I have to sign a long-term contract?",
              a: "No! We believe in earning your business every month. All our packages are month-to-month with no long-term commitment.",
            },
            {
              q: "Can I change packages later?",
              a: "Absolutely! You can upgrade or downgrade your package at any time. We'll help you find the right fit as your business grows.",
            },
            {
              q: "What's included in the free consultation?",
              a: "We'll review your current presence, analyze your local market, discuss your goals, and recommend the best package for your needs. No obligation.",
            },
            {
              q: "How quickly will I see results?",
              a: "Most clients see initial results within 30-60 days. However, sustainable growth typically takes 3-6 months. We focus on long-term success, not quick fixes.",
            },
          ].map((faq, index) => (
            <Fade key={index} delay={index * 0.1}>
              <div
                style={{
                  background: C.gray50,
                  padding: "24px",
                  borderRadius: "12px",
                }}
              >
                <Text
                  size="sm"
                  style={{
                    fontWeight: 600,
                    color: C.navy,
                    marginBottom: "12px",
                  }}
                >
                  {faq.q}
                </Text>
                <Text size="sm" style={{ color: C.gray600 }}>
                  {faq.a}
                </Text>
              </div>
            </Fade>
          ))}
        </div>
      </Section>

      {/* CTA Section */}
      <Section background={C.navyLight}>
        <Fade>
          <div style={{ textAlign: "center", maxWidth: "700px", margin: "0 auto" }}>
            <Heading light style={{ marginBottom: "24px" }}>
              Not Sure Which Package is Right for You?
            </Heading>
            <Text light size="lg" style={{ marginBottom: "40px" }}>
              Let's chat! We'll recommend the best package for your goals and budget during your free consultation.
            </Text>
            <Button variant="coral" size="lg" onClick={() => goToContact()}>
              Get Free Consultation
            </Button>
          </div>
        </Fade>
      </Section>
    </>
  );
}
